import React, { useState, useEffect, useRef } from "react";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import "../assets/css/EditLead.css";
import "../assets/css/AddLead.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import { formValueWithMobileSanitize, mobileValidationMessage, sanitizeMobileInput } from "../utils/phone";

// Qualification order: highest academic level to lowest (for dropdown)
const QUALIFICATION_ORDER = [
  "PhD", "M.Tech / ME", "MBA", "MCA", "M.Sc", "MA",
  "B.Tech / BE", "B.Sc", "BA", "BBA", "BCA",
  "Diploma", "Intermediate / 12th", "High School", "Other"
];
const sortQualificationsByLevel = (list) => {
  if (!Array.isArray(list)) return list;
  return [...list].sort((a, b) => {
    const nameA = (a.Qualification_Name || a.qualification_name || "").trim();
    const nameB = (b.Qualification_Name || b.qualification_name || "").trim();
    const idxA = QUALIFICATION_ORDER.findIndex((n) => n === nameA || n.toLowerCase() === nameA.toLowerCase());
    const idxB = QUALIFICATION_ORDER.findIndex((n) => n === nameB || n.toLowerCase() === nameB.toLowerCase());
    const orderA = idxA === -1 ? 999 : idxA;
    const orderB = idxB === -1 ? 999 : idxB;
    return orderA - orderB;
  });
};

function EditTrainingLead({ leadId, onBack, onUpdateLead, courses = [] }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    personalEmail: "",
    personalPhone: "",
    altPhone: "",
    qualification: "",
    customQualification: "",
    collegeName: "",
    yearOfPassing: "",
    expectedFee: "",
    earliestStartDate: "",
    batch: "",
    modeIds: [],
    source: "",
    status: "",
    assignedTo: "",
    courseId: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    zipCode: ""
  });

  const [followupData, setFollowupData] = useState({
    followupDate: "",
    followupType: "",
    nextFollowupDate: "",
    remarks: ""
  });
  const [followupId, setFollowupId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [qualifications, setQualifications] = useState([]);
  const [loadingQualifications, setLoadingQualifications] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [modes, setModes] = useState([]);
  const [loadingModes, setLoadingModes] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState("");
  const courseDropdownRef = useRef(null);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [sourceSearchQuery, setSourceSearchQuery] = useState("");
  const sourceDropdownRef = useRef(null);
  const [qualificationDropdownOpen, setQualificationDropdownOpen] = useState(false);
  const [qualificationSearchQuery, setQualificationSearchQuery] = useState("");
  const qualificationDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchQuery, setStatusSearchQuery] = useState("");
  const statusDropdownRef = useRef(null);


  // Fetch active DigiX staff users for Assigned To
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            id: u.user_id ?? u.User_Id ?? u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || `User #${u.user_id}`,
          }))
          .filter((u) => u.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(mapped);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch modes from API
  useEffect(() => {
    const fetchModes = async () => {
      setLoadingModes(true);
      try {
        const response = await axiosInstance.get("/modes");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((m) => ({
            id: m.Mode_Id ?? m.mode_id ?? m.id,
            name: m.Mode_Name ?? m.mode_name ?? m.name ?? ""
          }))
          .filter((m) => m.id != null && String(m.name || "").trim() !== "");
        setModes(mapped);
      } catch (error) {
        console.error("Error fetching modes:", error);
        setModes([]);
      } finally {
        setLoadingModes(false);
      }
    };
    fetchModes();
  }, []);

  // Fetch sources
  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const response = await axiosInstance.get("/lead-sources");
        if (response.data && Array.isArray(response.data)) {
          setSources(response.data);
        }
      } catch (error) {
        console.error("Error fetching sources:", error);
      } finally {
        setLoadingSources(false);
      }
    };

    fetchSources();
  }, []);

  // Fetch training lead statuses from API (same as filter section in DigixLeads)
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get('/lead-statuses');
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? ""
          }))
          .filter((s) => s.id != null && s.name.trim() !== "");
        setLeadStatuses(mapped);
      } catch (error) {
        console.error("Error fetching lead statuses for training lead (edit):", error);
        setLeadStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  // Fetch qualifications from API
  useEffect(() => {
    const fetchQualifications = async () => {
      setLoadingQualifications(true);
      try {
        const response = await axiosInstance.get('/qualifications');
        if (response.data && Array.isArray(response.data)) {
          setQualifications(sortQualificationsByLevel(response.data));
        }
      } catch (error) {
        console.error("Error fetching qualifications:", error);
      } finally {
        setLoadingQualifications(false);
      }
    };

    fetchQualifications();
  }, []);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await axiosInstance.get("/countries");
        if (response.data && Array.isArray(response.data)) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // When countries and country name are known but phoneCountryCode is still empty,
  // derive a sensible default code from the selected country so the code dropdown shows it,
  // and ensure the personalPhone field does not duplicate that code.
  useEffect(() => {
    if (phoneCountryCode || !formData.country || !countries.length) {
      return;
    }
    const matchedCountry = countries.find(
      (c) => c.country_name === formData.country
    );
    if (matchedCountry) {
      const code = (matchedCountry.country_code || matchedCountry.country_short_code || "").trim();
      if (code) {
        setPhoneCountryCode(code);
        setFormData((prev) => {
          let v = prev.personalPhone || "";
          if (typeof v === "string" && v) {
            const plainCode = code.startsWith("+") ? code.slice(1) : code;
            let next = v.trim();
            if (next.startsWith(code)) {
              next = next.slice(code.length).trim();
            } else if (next.startsWith(plainCode)) {
              next = next.slice(plainCode.length).trim();
            }
            return { ...prev, personalPhone: next };
          }
          return prev;
        });
      }
    }
  }, [phoneCountryCode, formData.country, countries]);

  // Close course dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(e.target)) {
        setIsCourseDropdownOpen(false);
        setCourseSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) { setSourceDropdownOpen(false); setSourceSearchQuery(""); }
      if (qualificationDropdownRef.current && !qualificationDropdownRef.current.contains(e.target)) { setQualificationDropdownOpen(false); setQualificationSearchQuery(""); }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) { setStatusDropdownOpen(false); setStatusSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch states when country is selected (same as Add Lead: /states/{country_id})
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedCountryId) {
        setStates([]);
        setFormData(prev => ({ ...prev, state: "" }));
        return;
      }
      setLoadingStates(true);
      try {
        const response = await axiosInstance.get(`/states/${selectedCountryId}`);
        if (response.data && Array.isArray(response.data)) {
          setStates(response.data);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        try {
          const altResponse = await axiosInstance.get(`/api/states/${selectedCountryId}`);
          if (altResponse.data && Array.isArray(altResponse.data)) {
            setStates(altResponse.data);
          }
        } catch (altError) {
          console.error("Error with alternative endpoint:", altError);
          setStates([]);
        }
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [selectedCountryId]);

  // Fetch cities when state is selected (same as Add Lead: /cities/{state_id})
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedStateId) {
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
        return;
      }
      setLoadingCities(true);
      try {
        const response = await axiosInstance.get(`/cities/${selectedStateId}`);
        if (response.data && Array.isArray(response.data)) {
          setCities(response.data);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, [selectedStateId]);

  // Fetch lead details and pre-fill form
  useEffect(() => {
    if (!leadId) {
      console.error("EditTrainingLead: No leadId provided");
      setLoading(false);
      alert("Error: No lead ID provided. Please go back and try again.");
      return;
    }
    
    const fetchLeadDetails = async (categories) => {
      setLoading(true);
      let dataLoaded = false;
      
      try {
        // Try single lead endpoint
        let leadObj = null;
        let addressObj = {};

        try {
          // Training lead endpoint
          const endpoint = `/training-lead/${leadId}`;
          const response = await axiosInstance.get(endpoint);
          
          if (response.data) {
            // Check different response structures
            if (response.data.training_lead) {
              leadObj = response.data.training_lead;
              addressObj = response.data.address || {};
            } else if (response.data.data && response.data.data.training_lead) {
              leadObj = response.data.data.training_lead;
              addressObj = response.data.data.address || {};
            } else if (response.data.data) {
              leadObj = response.data.data;
              addressObj = response.data.data.address || response.data.address || {};
            } else {
              leadObj = response.data;
              addressObj = response.data.address || {};
            }
          }
        } catch (err) {
          const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || "Error fetching training lead.";
          console.error("Error fetching single training lead:", errMsg);
          
          try {
            const allResponse = await axiosInstance.get("/training-leads");
            
            if (allResponse.data && Array.isArray(allResponse.data)) {
              const found = allResponse.data.find(item => {
                const l = item.training_lead || item;
                const id = l.Training_Lead_Id || l.training_lead_id || l.Training_Lead_id || l.id || l.ID;
                const leadIdNum = parseInt(leadId);
                const idNum = parseInt(id);
                return idNum === leadIdNum || String(id) === String(leadId) || id === leadId;
              });
              
              if (found) {
                leadObj = found.training_lead || found;
                addressObj = found.address || {};
              }
            } else if (allResponse.data && allResponse.data.results && Array.isArray(allResponse.data.results)) {
              const found = allResponse.data.results.find(item => {
                const l = item.training_lead || item;
                const id = l.Training_Lead_Id || l.training_lead_id || l.Training_Lead_id || l.id || l.ID;
                const leadIdNum = parseInt(leadId);
                const idNum = parseInt(id);
                return idNum === leadIdNum || String(id) === String(leadId) || id === leadId;
              });
              
              if (found) {
                leadObj = found.training_lead || found;
                addressObj = found.address || {};
              }
            }
          } catch (fallbackErr) {
            const fallbackMsg = fallbackErr.response?.data?.message || fallbackErr.message || "Fallback fetch failed.";
            console.error("Error in fallback fetch:", fallbackMsg);
          }
        }

        if (leadObj) {
          console.log("Setting form data with leadObj:", leadObj);
          console.log("Address object:", addressObj);
          
          setLeadName(`${leadObj.First_Name || leadObj.first_name || ""} ${leadObj.Last_Name || leadObj.last_name || ""}`.trim());

          // Training Lead: course(s) can be an array now (Courses: [{Course_Id,...}, ...])
          const coursesArr = Array.isArray(leadObj.Courses) ? leadObj.Courses : [];
          const courseIdsFromCourses = coursesArr
            .map((c) => c.Course_Id ?? c.course_id ?? c.id ?? null)
            .filter((id) => id != null);
          const singleCourseId =
            (coursesArr[0] && (coursesArr[0].Course_Id ?? coursesArr[0].course_id)) ??
            (leadObj.Course_Id || leadObj.course_id || leadObj.CourseId || "");

          // Get Qualification_Id - check multiple field name variations
          const qualificationId = leadObj.Qualification_Id || leadObj.qualification_id || leadObj.QualificationId;
          const qualificationIdStr = qualificationId ? String(qualificationId) : "";
          // Check if it's "Other" (15) and get custom qualification name
          const qualificationName = leadObj.Other_Qualification || leadObj.other_qualification || leadObj.Qualification_Name || leadObj.qualification_name || "";
          const isOther = qualificationId === 15 || qualificationIdStr === "15";
          const customQualificationValue = isOther && qualificationName ? qualificationName : "";

          const apiCountryCode = leadObj.Country_Code || leadObj.country_code || "";
          if (apiCountryCode) {
            setPhoneCountryCode(apiCountryCode);
          }

          const rawPhone = leadObj.Phone || leadObj.phone || "";
          const normalizedPhone = (() => {
            if (!rawPhone) return "";
            if (apiCountryCode && String(rawPhone).startsWith(String(apiCountryCode))) {
              return String(rawPhone).slice(String(apiCountryCode).length);
            }
            return String(rawPhone);
          })();

          setFormData({
            firstName: leadObj.First_Name || leadObj.first_name || "",
            lastName: leadObj.Last_Name || leadObj.last_name || "",
            personalEmail: leadObj.Email || leadObj.email || "",
            personalPhone: normalizedPhone,
            altPhone: leadObj.Alt_Phone || leadObj.alt_phone || "",
            qualification: qualificationIdStr,
            customQualification: customQualificationValue,
            collegeName: leadObj.College_Name || leadObj.college_name || "",
            yearOfPassing: leadObj.Year_Of_Passing || leadObj.year_of_passing || "",
            expectedFee: leadObj.Expected_Fee || leadObj.expected_fee || "",
            earliestStartDate: (() => {
              const dateValue = leadObj.Earliest_Start_Date || leadObj.earliest_start_date || leadObj.EarliestStartDate || "";
              // Convert date to YYYY-MM-DD format for HTML date input
              if (dateValue) {
                try {
                  const date = new Date(dateValue);
                  if (!isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  }
                } catch (e) {
                  console.error("Error parsing date:", dateValue, e?.message || e);
                  if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return dateValue;
                  }
                }
              }
              return "";
            })(),
            batch: leadObj.Batch || leadObj.batch || "",
            modeIds: (() => {
              const arr = leadObj.Mode_Ids ?? leadObj.mode_ids ?? leadObj.Modes ?? leadObj.modes;
              if (Array.isArray(arr) && arr.length > 0) {
                return arr
                  .map((x) => (typeof x === "object" && x != null ? (x.Mode_Id ?? x.mode_id ?? x.id ?? null) : x))
                  .filter((id) => id != null);
              }
              const single = leadObj.Mode ?? leadObj.mode;
              return single ? [single] : [];
            })(),
            source: (leadObj.Source_Id || leadObj.source_id || leadObj.SourceId) ? String(leadObj.Source_Id || leadObj.source_id || leadObj.SourceId) : "",
            status: (leadObj.Status_Id || leadObj.status_id || leadObj.StatusId) ? String(leadObj.Status_Id || leadObj.status_id || leadObj.StatusId) : "",
            assignedTo: (leadObj.Assigned_To ?? leadObj.assigned_to) != null && (leadObj.Assigned_To ?? leadObj.assigned_to) !== "" ? String(leadObj.Assigned_To ?? leadObj.assigned_to) : "",
            courseId: "",
            addressLine1: addressObj.Address_Line_1 || addressObj.address_line_1 || "",
            addressLine2: addressObj.Address_Line_2 || addressObj.address_line_2 || "",
            city: addressObj.City_Name || addressObj.city_name || addressObj.City || addressObj.city || "",
            state: addressObj.State_Name || addressObj.state_name || addressObj.State || addressObj.state || "",
            country: addressObj.Country_Name || addressObj.country_name || addressObj.Country || addressObj.country || "",
            zipCode: addressObj.Zip_Code || addressObj.zip_code || addressObj.ZipCode || addressObj.zipCode || ""
          });
          
          if (Array.isArray(leadObj.Course_Ids || leadObj.course_ids)) {
            setSelectedCourseIds((leadObj.Course_Ids || leadObj.course_ids).map((id) => Number(id)));
          } else if (courseIdsFromCourses.length > 0) {
            setSelectedCourseIds(courseIdsFromCourses.map((id) => Number(id)));
          } else if (singleCourseId) {
            setSelectedCourseIds([Number(singleCourseId)]);
          }

          dataLoaded = true;

          const countryId = addressObj.Country_Id || addressObj.country_id || addressObj.CountryId;
          const stateId = addressObj.State_Id || addressObj.state_id || addressObj.StateId;
          
          if (countryId) {
            setSelectedCountryId(countryId);
          }
          if (stateId) {
            setSelectedStateId(stateId);
          }

          // Fetch latest followup
          try {
            // For training leads, use training-lead-specific endpoint with path parameter
            const followupRes = await axiosInstance.get(`/training-followups/${leadId}`);
            
            if (followupRes.data && Array.isArray(followupRes.data) && followupRes.data.length > 0) {
              // Sort by Followup_Date descending to get the latest
              const sortedFollowups = followupRes.data.sort((a, b) => {
                const dateA = new Date(a.Followup_Date);
                const dateB = new Date(b.Followup_Date);
                return dateB - dateA; // Descending order
              });
              const latest = sortedFollowups[0];
              // Format datetime for datetime-local input (YYYY-MM-DDTHH:mm)
              const formatDateTimeLocal = (dateString) => {
                if (!dateString) return "";
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              };
              setFollowupData({
                followupDate: formatDateTimeLocal(latest.Followup_Date),
                followupType: latest.Followup_Type || "",
                nextFollowupDate: formatDateTimeLocal(latest.Next_Followup_Date),
                remarks: latest.Remarks || ""
              });
              setFollowupId(latest.Followup_Id || latest.Followup_id);
            }
          } catch (followupError) {
            const followupMsg = followupError.response?.data?.message || followupError.message || "Error fetching followup.";
            console.error("Error fetching followup:", followupMsg);
          }
        } else {
          console.error("EditTrainingLead: Failed to load training lead details.");
          alert("Failed to load training lead details. The lead may not exist or the API endpoint may be incorrect.");
        }
      } catch (error) {
        const msg = error.response?.data?.error || error.response?.data?.details || error.message || "Unknown error";
        console.error("Error fetching lead details:", msg);
        alert("Error loading training lead: " + msg);
      } finally {
        setLoading(false);
      }
    };

    const loadData = async () => {
      await fetchLeadDetails([]);
    };

    if (leadId) {
      loadData();
    }
  }, [leadId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear customQualification when switching away from "Other"
    if (name === "qualification" && value !== "15") {
      setFormData(prev => ({ ...prev, [name]: value, customQualification: "" }));
      return;
    }

    if (name === "country") {
      const selectedCountry = countries.find(c => c.country_name === value);
      if (selectedCountry) {
        setSelectedCountryId(selectedCountry.country_id);
      } else {
        setSelectedCountryId(null);
        setSelectedStateId(null);
        setStates([]);
        setCities([]);
        setFormData(prev => ({ ...prev, state: "", city: "" }));
      }
    }

    if (name === "state") {
      const selectedState = states.find(s => s.state_name === value);
      if (selectedState) {
        setSelectedStateId(selectedState.state_id);
      } else {
        setSelectedStateId(null);
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
      }
    }

    let nextValue = type === "checkbox" ? checked : formValueWithMobileSanitize(name, value);

    // For personal phone, strip leading country code if the user includes it in the number box
    if (name === "personalPhone" && typeof nextValue === "string" && phoneCountryCode) {
      const code = phoneCountryCode.trim();
      if (code) {
        let v = nextValue.trim();
        const plainCode = code.startsWith("+") ? code.slice(1) : code;
        if (v.startsWith(code)) {
          v = v.slice(code.length).trim();
        } else if (v.startsWith(plainCode)) {
          v = v.slice(plainCode.length).trim();
        }
        nextValue = sanitizeMobileInput(v);
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleFollowupChange = (e) => {
    const { name, value } = e.target;
    setFollowupData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleToggleCourse = (courseId) => {
    setSelectedCourseIds((prev) => {
      if (prev.includes(courseId)) {
        return prev.filter((id) => id !== courseId);
      }
      return [...prev, courseId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName) {
      alert("Please fill in required fields (First Name and Phone).");
      return;
    }
    const phoneError = mobileValidationMessage(formData.personalPhone, { required: true, label: "Phone" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }
    if (!formData.source) {
      alert("Please select a source.");
      return;
    }
    if (!selectedCourseIds || selectedCourseIds.length === 0) {
      alert("Please select at least one course.");
      return;
    }

    let createdBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (error) {
      console.error("Error reading user data:", error);
    }

    // Find city_id, state_id, country_id from names
    const selectedCountry = countries.find(c => c.country_name === formData.country);
    const selectedState = states.find(s => s.state_name === formData.state);
    const selectedCity = cities.find(c => c.city_name === formData.city);

    // Training Lead: Use payload for /update-training-lead endpoint with address fields
    const endpoint = `/update-training-lead/${leadId}`;
    const payload = {
      First_Name: formatPascal(formData.firstName),
      Last_Name: formatPascal(formData.lastName),
      Phone: formData.personalPhone || null,
      Alt_Phone: formData.altPhone || null,
      Email: formData.personalEmail || null,
      Qualification_Id: formData.qualification ? parseInt(formData.qualification, 10) : null,
      ...(String(formData.qualification) === "15" && formData.customQualification
        ? { Other_Qualification: formData.customQualification }
        : {}),
      College_Name: formData.collegeName ? formatPascal(formData.collegeName) : null,
      Year_Of_Passing: formData.yearOfPassing || null,
      Expected_Fee: formData.expectedFee || null,
      Earliest_Start_Date: formData.earliestStartDate || null,
      Batch: formData.batch || null,
      Course_Ids: selectedCourseIds.length ? selectedCourseIds.map((id) => parseInt(id, 10)) : null,
      Mode_Ids: (() => {
        const raw = Array.isArray(formData.modeIds) ? formData.modeIds : [];
        if (raw.length === 0) return null;
        const ids = raw
          .map((x) => (typeof x === "number" ? x : (modes.find((m) => m.name === x || m.id === x)?.id ?? (Number(x) || null))))
          .filter((id) => id != null);
        return ids.length > 0 ? ids : null;
      })(),
      Status_Id: formData.status ? parseInt(formData.status) : null,
      Assigned_To: formData.assignedTo ? parseInt(formData.assignedTo, 10) : null,
      Country_Code: phoneCountryCode || null,
      Address_Line_1: formData.addressLine1 || null,
      Address_Line_2: formData.addressLine2 || null,
      City_Id: selectedCity ? selectedCity.city_id : null,
      State_Id: selectedState ? selectedState.state_id : null,
      Country_Id: selectedCountry ? selectedCountry.country_id : null,
      Zip_Code: formData.zipCode || null
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.put(endpoint, payload);
      if (response.data) {
        // Create/update followup if followup data is provided
        if (followupData.followupDate) {
          try {
            // Format dates to ISO string format (YYYY-MM-DDTHH:mm:ss)
            const formatDateTime = (dateTimeString) => {
              if (!dateTimeString) return null;
              // datetime-local input returns format: YYYY-MM-DDTHH:mm
              // API expects: YYYY-MM-DDTHH:mm:ss
              return dateTimeString + ":00";
            };

            // Get Updated_By from localStorage
            let updatedBy = createdBy;
            try {
              const userStr = getAuthItem("user");
              if (userStr) {
                const user = JSON.parse(userStr);
                updatedBy = user.user_id || user.id || createdBy;
              }
            } catch (error) {
              console.error("Error reading user data for Updated_By:", error);
            }

            if (followupId) {
              // Update existing followup for training leads
              const trainingFollowupPayload = {
                Training_Lead_Id: leadId,
                Followup_Date: formatDateTime(followupData.followupDate),
                Followup_Type: followupData.followupType || null,
                Remarks: followupData.remarks || null,
                Next_Followup_Date: formatDateTime(followupData.nextFollowupDate) || null,
                Updated_By: updatedBy
              };
              await axiosInstance.put(`/update-training-followup/${followupId}`, trainingFollowupPayload);
            } else {
              // Create new followup
              const followupPayload = {
                Lead_Id: null,
                client_id: null,
                Training_Lead_Id: leadId,
                Followup_Date: formatDateTime(followupData.followupDate),
                Followup_Type: followupData.followupType || null,
                Remarks: followupData.remarks || null,
                Next_Followup_Date: formatDateTime(followupData.nextFollowupDate) || null,
                Updated_By: updatedBy,
                Created_By: createdBy
              };
              await axiosInstance.post("/create-training-followup", followupPayload);
            }
          } catch (followupError) {
            console.error("Error creating/updating followup:", followupError);
            // Don't fail the whole update if followup creation/update fails
          }
        }

        alert("Lead updated successfully!");
        // Call parent callback to refresh leads list
        if (onUpdateLead) {
          onUpdateLead();
        }
        // Navigate back to training leads page
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to update lead.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="editlead-container">
        <div className="editlead-loading">Loading lead details...</div>
      </div>
    );
  }

  return (
    <div className="editlead-container">
      <button className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back 
      </button>
      <div className="issues-main-card">
      <form className="editlead-form" onSubmit={handleSubmit}>
        {/* Contact Details */}
        <div className="form-section">
          <h2 className="section-title">Contact Details</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Personal Email</label>
              <input type="email" name="personalEmail" value={formData.personalEmail} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Source <span className="required">*</span></label>
              <div className="domain-filter-dropdown" ref={sourceDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", cursor: "pointer" }} onClick={() => setSourceDropdownOpen((o) => !o)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSourceDropdownOpen((o) => !o); } }}>
                  <span>{formData.source ? ((() => { const src = sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source)); return src?.source_name || src?.Source_Name || "Select Source"; })()) : "Select Source"}</span>
                  <FaChevronDown className={`domain-filter-chevron ${sourceDropdownOpen ? "open" : ""}`} />
                </div>
                {sourceDropdownOpen && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search sources..." value={sourceSearchQuery} onChange={(e) => setSourceSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.source ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: "" })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>Select Source</div>
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).map((s) => {
                        const sid = String(s.source_id ?? s.Source_Id ?? s.id);
                        const sname = s.source_name || s.Source_Name || "";
                        return (
                          <div key={sid} className={`domain-filter-option ${String(formData.source) === sid ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: sid })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>{sname}</div>
                        );
                      })}
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No sources found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Personal Phone <span className="required">*</span></label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  style={{ width: "80px", flexShrink: 0 }}
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                >
                  <option value="">Code</option>
                  {countries.map((country) => {
                    const code = (country.country_code || country.country_short_code || "").trim();
                    return (
                      <option key={country.country_id} value={code}>
                        {code}
                      </option>
                    );
                  })}
                </select>
                <input
                  style={{ flex: 1 }}
                  type="text"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alternate Phone</label>
              <input
                type="text"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="domain-filter-dropdown" ref={statusDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingStatuses ? 0.6 : 1, cursor: loadingStatuses ? "default" : "pointer" }} onClick={() => !loadingStatuses && setStatusDropdownOpen((o) => !o)} role="button" tabIndex={loadingStatuses ? -1 : 0} onKeyDown={(e) => { if (!loadingStatuses && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setStatusDropdownOpen((o) => !o); } }}>
                  <span>{loadingStatuses ? "Loading statuses..." : (formData.status ? (leadStatuses.find((s) => String(s.id) === String(formData.status))?.name || "Select Status") : "Select Status")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${statusDropdownOpen ? "open" : ""}`} />
                </div>
                {statusDropdownOpen && !loadingStatuses && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search statuses..." value={statusSearchQuery} onChange={(e) => setStatusSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.status ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: "" })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>Select Status</div>
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).map((s) => (
                        <div key={s.id} className={`domain-filter-option ${String(formData.status) === String(s.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: String(s.id) })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>{s.name}</div>
                      ))}
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No statuses found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleInputChange}
                disabled={loadingUsers}
              >
                {loadingUsers && <option value="">Loading users...</option>}
                {!loadingUsers && <option value="">Select user</option>}
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Training Details */}
        <div className="form-section">
          <h2 className="section-title">Training Details</h2>
          <div className="form-grid">
            <div className="form-group" ref={courseDropdownRef}>
              <label>Courses <span className="required">*</span></label>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsCourseDropdownOpen((open) => !open);
                  }
                }}
                className="permissions-select-trigger"
                onClick={() => setIsCourseDropdownOpen((open) => !open)}
              >
                <span className="permissions-select-text">
                  {(!courses || courses.length === 0) ? "No courses available"
                    : selectedCourseIds.length === 0
                      ? "Select Courses"
                      : `${selectedCourseIds.length} course(s) selected`}
                </span>
                <FaChevronDown
                  className={`permissions-select-chevron ${isCourseDropdownOpen ? "open" : ""}`}
                />
              </div>
              {isCourseDropdownOpen && courses && courses.length > 0 && (
                <div className="permissions-select-panel" style={{ maxHeight: 220, overflowY: "auto" }}>
                  <input type="text" className="domain-filter-search" placeholder="Search courses..." value={courseSearchQuery} onChange={(e) => setCourseSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} style={{ margin: "8px", width: "calc(100% - 16px)", boxSizing: "border-box" }} />
                  <label
                    className="permissions-select-item"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedCourseIds.length ===
                        courses.filter((c) => c.Status === "Active").length
                      }
                      onChange={() => {
                        const activeCourses = (courses || []).filter((c) => c.Status === "Active");
                        if (selectedCourseIds.length === activeCourses.length) {
                          setSelectedCourseIds([]);
                        } else {
                          setSelectedCourseIds(activeCourses.map((c) => c.Course_Id));
                        }
                      }}
                    />
                    <span className="permissions-select-item-label">Select all</span>
                  </label>
                  {courses
                    .filter((course) => course.Status === "Active")
                    .filter((course) => !courseSearchQuery.trim() || String((course.Course_Code || "") + " " + (course.Course_Name || "")).toLowerCase().includes(courseSearchQuery.trim().toLowerCase()))
                    .map((course) => (
                      <label
                        key={course.Course_Id}
                        className="permissions-select-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCourseIds.includes(course.Course_Id)}
                          onChange={() => handleToggleCourse(course.Course_Id)}
                        />
                        <span className="permissions-select-item-label">
                          {course.Course_Code} - {course.Course_Name}
                        </span>
                      </label>
                    ))}
                  {courses.filter((course) => course.Status === "Active").filter((course) => !courseSearchQuery.trim() || String((course.Course_Code || "") + " " + (course.Course_Name || "")).toLowerCase().includes(courseSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results" style={{ padding: "12px" }}>No courses found</div>}
                </div>
              )}
              {selectedCourseIds.length > 0 && courses && (
                <div style={{ marginTop: 8 }}>
                  {selectedCourseIds.map((id) => {
                    const course = courses.find((c) => c.Course_Id === id);
                    if (!course || !course.Description) return null;
                    return (
                      <p
                        key={id}
                        className="course-description"
                        style={{ marginTop: 4, fontSize: "13px", color: "#7A8CA5" }}
                      >
                        {course.Course_Code} - {course.Course_Name}: {course.Description}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Mode</label>
              {loadingModes ? (
                <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>Loading modes...</p>
              ) : modes.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 16px", alignItems: "center" }}>
                  {modes.map((mode) => (
                    <label key={mode.id} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={(formData.modeIds || []).includes(mode.id) || (formData.modeIds || []).includes(mode.name)}
                        onChange={(e) => {
                          const prevIds = Array.isArray(formData.modeIds) ? [...formData.modeIds] : [];
                          const alreadySelected = prevIds.includes(mode.id) || prevIds.includes(mode.name);
                          const nextIds = e.target.checked
                            ? (alreadySelected ? prevIds : [...prevIds, mode.id])
                            : prevIds.filter((id) => id !== mode.id && id !== mode.name);
                          setFormData((prev) => ({ ...prev, modeIds: nextIds }));
                        }}
                        style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#2977ed" }}
                      />
                      <span>{mode.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: "13px", color: "#999" }}>No modes available</p>
              )}
            </div>
            <div className="form-group">
              <label>Qualification</label>
              <div className="domain-filter-dropdown" ref={qualificationDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingQualifications ? 0.6 : 1, cursor: loadingQualifications ? "default" : "pointer" }} onClick={() => !loadingQualifications && setQualificationDropdownOpen((o) => !o)} role="button" tabIndex={loadingQualifications ? -1 : 0} onKeyDown={(e) => { if (!loadingQualifications && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setQualificationDropdownOpen((o) => !o); } }}>
                  <span>{loadingQualifications ? "Loading qualifications..." : (formData.qualification ? ((() => { const q = qualifications.find((q) => String(q.Qualification_Id ?? q.qualification_id ?? q.id) === String(formData.qualification)); return q?.Qualification_Name || q?.qualification_name || "Select Qualification"; })()) : "Select Qualification")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${qualificationDropdownOpen ? "open" : ""}`} />
                </div>
                {qualificationDropdownOpen && !loadingQualifications && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search qualifications..." value={qualificationSearchQuery} onChange={(e) => setQualificationSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.qualification ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, qualification: "", customQualification: "" })); setQualificationDropdownOpen(false); setQualificationSearchQuery(""); }}>Select Qualification</div>
                      {qualifications.filter((q) => !qualificationSearchQuery.trim() || String(q.Qualification_Name || q.qualification_name || "").toLowerCase().includes(qualificationSearchQuery.trim().toLowerCase())).map((q) => {
                        const qid = String(q.Qualification_Id ?? q.qualification_id ?? q.id);
                        const qname = q.Qualification_Name || q.qualification_name || "";
                        return (
                          <div key={qid} className={`domain-filter-option ${String(formData.qualification) === qid ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, qualification: qid, customQualification: qid === "15" ? p.customQualification : "" })); setQualificationDropdownOpen(false); setQualificationSearchQuery(""); }}>{qname}</div>
                        );
                      })}
                      {qualifications.filter((q) => !qualificationSearchQuery.trim() || String(q.Qualification_Name || q.qualification_name || "").toLowerCase().includes(qualificationSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No qualifications found</div>}
                    </div>
                  </div>
                )}
              </div>
              {formData.qualification === "15" && (
                <input
                  type="text"
                  name="customQualification"
                  value={formData.customQualification}
                  onChange={handleInputChange}
                  
                  style={{ marginTop: "8px", width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              )}
            </div>
            <div className="form-group">
              <label>College Name</label>
              <input
                type="text"
                name="collegeName"
                value={formData.collegeName}
                onChange={handleInputChange}
               
              />
            </div>
            <div className="form-group">
              <label>Year Of Passing</label>
              <input
                type="text"
                name="yearOfPassing"
                value={formData.yearOfPassing}
                onChange={handleInputChange}
               
              />
            </div>
            <div className="form-group">
              <label>Expected Fee</label>
              <input
                type="text"
                name="expectedFee"
                value={formData.expectedFee}
                onChange={handleInputChange}
                
              />
            </div>
            <div className="form-group">
              <label>Earliest Start Date</label>
              <input
                type="date"
                name="earliestStartDate"
                value={formData.earliestStartDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Batch</label>
              <select
                name="batch"
                value={formData.batch || ""}
                onChange={handleInputChange}
                className="form-select"
                style={{ width: "100%", padding: "8px 12px", borderRadius: "4px", border: "1px solid #ddd", fontSize: "14px" }}
              >
                <option value="">Select</option>
                <option value="Morning">Morning</option>
                <option value="Afternoon">Afternoon</option>
                <option value="Evening">Evening</option>
              </select>
            </div>
                                  </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <h2 className="section-title">Address</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select name="country" value={formData.country} onChange={handleInputChange} disabled={loadingCountries}>
                <option value="">{formData.country || "Select Country"}</option>
                {countries.map(country => (
                  <option key={country.country_id} value={country.country_name}>{country.country_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select name="state" value={formData.state} onChange={handleInputChange} disabled={!selectedCountryId || loadingStates}>
                <option value="">{formData.state || "Select State"}</option>
                {states.map(state => (
                  <option key={state.state_id} value={state.state_name}>{state.state_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select name="city" value={formData.city} onChange={handleInputChange} disabled={!selectedStateId || loadingCities}>
                <option value="">{formData.city || "Select City"}</option>
                {cities.map(city => (
                  <option key={city.city_id} value={city.city_name}>{city.city_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} />
            </div>
          </div>
        </div>

        {/* Latest Followup */}
        <div className="editlead-followup-section">
          <div className="editlead-followup-grid">
            <div className="form-group">
              <label>Followup Date</label>
              <input
                type="datetime-local"
                name="followupDate"
                value={followupData.followupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Followup Type</label>
              <input
                type="text"
                name="followupType"
                value={followupData.followupType}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group">
              <label>Next Followup Date</label>
              <input
                type="datetime-local"
                name="nextFollowupDate"
                value={followupData.nextFollowupDate}
                onChange={handleFollowupChange}
              />
            </div>
            <div className="form-group full-width">
              <label>Remarks</label>
              <textarea
                name="remarks"
                value={followupData.remarks}
                onChange={handleFollowupChange}
                rows="3"
              />
            </div>
          </div>
        </div>

        <div className="editlead-actions">
          <button type="submit" className="btn-update-lead" disabled={submitting}>
            {submitting ? "Updating..." : "Update Lead"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default EditTrainingLead;
